import {Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, inject} from '@angular/core';
import {DxPieChartModule} from 'devextreme-angular';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BlockedStockScoreAggregateDto, ThemeService} from '@services';
import {hasScoreData, NoData, noDataColor, noDataRing} from '../../../../../components/library/doughnut-chart/score-center';

export interface DoughnutSegmentConfig {
    argument: string;
    valueField?: string;
    combineFields?: string[];
    color: string;
}

export type DoughnutCenterMode = 'largest' | 'total';

@Component({
    selector: 'app-blocked-stock-doughnut-chart',
    imports: [DxPieChartModule],
    templateUrl: './blocked-stock-doughnut-chart.component.html',
    styles: `
        dx-pie-chart {
            height: 350px;
        }
    `,
})
export class BlockedStockDoughnutChartComponent implements OnInit, OnChanges {
    private themeService = inject(ThemeService);
    private destroyRef = inject(DestroyRef);

    @Input() title: string = '';
    // null until loaded, so the centre stays blank instead of flashing No Data.
    @Input() data: BlockedStockScoreAggregateDto[] | null = null;
    @Input() segments: DoughnutSegmentConfig[] = [];
    @Input() centerMode: DoughnutCenterMode = 'largest';
    @Input() centerLabel: string = '';
    @Input() totalField?: string;

    items: { argument: string; value: number; color: string }[] = [];
    chartItems: { argument: string; value: number }[] = [];
    hasData = false;
    centerValue: string = '';
    centerText: string = '';

    ngOnInit(): void {
        this.themeService.isDark$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.calculate());
    }

    ngOnChanges(_changes: SimpleChanges): void {
        this.calculate();
    }

    private calculate(): void {
        const data = this.data;
        const segments = this.segments ?? [];

        if (!data || segments.length === 0) {
            this.items = [];
            this.chartItems = [];
            this.hasData = false;
            this.centerValue = '';
            this.centerText = '';
            return;
        }

        this.items = segments.map(seg => {
            let value = 0;
            for (const dto of data) {
                if (seg.combineFields && seg.combineFields.length > 0) {
                    for (const f of seg.combineFields) {
                        value += this.toNumber((dto as any)[f]);
                    }
                } else if (seg.valueField) {
                    value += this.toNumber((dto as any)[seg.valueField]);
                }
            }
            return {argument: seg.argument, value, color: seg.color};
        });

        this.hasData = hasScoreData(this.items);
        // The zero slices stay in the data source so the legend still lists every category.
        this.chartItems = this.hasData ? this.items : [...this.items, ...noDataRing()];

        const slicesSum = this.items.reduce((s, i) => s + i.value, 0);

        if (!this.hasData) {
            this.centerValue = NoData;
            this.centerText = this.centerMode === 'total' ? this.centerLabel : '';
        } else if (this.centerMode === 'largest') {
            const largest = this.items.reduce(
                (max, item) => (item.value > max.value ? item : max),
                this.items[0]
            );
            const percent = (largest.value / slicesSum) * 100;
            this.centerValue = `${Math.round(percent)}%`;
            this.centerText = largest.argument;
        } else {
            const total = this.totalField
                ? data.reduce((s, d) => s + this.toNumber((d as any)[this.totalField!]), 0)
                : slicesSum;
            this.centerValue = this.formatAbbreviated(total);
            this.centerText = this.centerLabel;
        }
    }

    private toNumber(value: any): number {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
    }

    private formatAbbreviated(value: number): string {
        const abs = Math.abs(value);
        if (abs >= 1_000_000) {
            return `${this.trimZero((value / 1_000_000).toFixed(1))}M`;
        }
        if (abs >= 1_000) {
            return `${this.trimZero((value / 1_000).toFixed(1))}K`;
        }
        return `${Math.round(value)}`;
    }

    private trimZero(s: string): string {
        return s.replace(/\.0$/, '');
    }

    customizePoint = (pointInfo: any) => {
        if (pointInfo.argument === NoData)
            return {color: noDataColor};

        const item = this.items.find(i => i.argument === pointInfo.argument);
        return item ? {color: item.color} : {};
    };

    customizeLegendItems = (legendItems: any[]) => legendItems.filter(item => item.text !== NoData);

    getMarkerColor = (legendItem: any): string => {
        const item = this.items.find(i => i.argument === legendItem.text);
        return item?.color;
    };

    customizeTooltip = (info: any) => ({
        text: `${info.argumentText}: ${this.formatAbbreviated(info.value)}`
    });
}
